import { useCallback, useMemo, useState } from "react";

let nextKey = 0;
const newKey = () => `line-${(nextKey += 1)}`;

const amount = (value) => {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * A quantity of whole units, or NaN. "1.5" or "2x" must not quietly become 1
 * or 2: fewer units than the cashier typed would be recorded as delivered.
 */
const wholeQuantity = (value) => {
  const text = String(value ?? "").trim();
  return /^\d+$/.test(text) ? Number(text) : NaN;
};

/**
 * How many of a line go to the warehouse: all of it, none, or the part typed.
 * NaN when a split is not a whole number between one and the quantity less one.
 */
const warehousePart = (line) => {
  const quantity = wholeQuantity(line.quantity);

  if (line.destination === "warehouse") return quantity;
  if (line.destination !== "split") return 0;

  const part = wholeQuantity(line.to_warehouse);
  return part >= 1 && part < quantity ? part : NaN;
};

/**
 * The lines of a delivery being recorded, shared by the dashboard's
 * Deliveries screen and the register's delivery sheet.
 *
 * A line is a product the shop stocks, or a new one (name + category) that
 * arrives as a draft. Its price is the whole lot's — "12 × Mango, 138" — and
 * optional: a lot without one still counts the stock, and waits for its price.
 */
export const useDeliveryLines = () => {
  const [lines, setLines] = useState([]);

  const addProduct = useCallback((product) => {
    setLines((current) =>
      current.some((line) => line.product_id === product.id)
        ? current
        : [
            ...current,
            {
              key: newKey(),
              product_id: product.id,
              name: product.name,
              quantity: "",
              line_total: "",
              destination: "shop",
              to_warehouse: "",
            },
          ],
    );
  }, []);

  const addNewProduct = useCallback((name, category) => {
    setLines((current) => [
      ...current,
      {
        key: newKey(),
        new_product: { name: name.trim(), category },
        name: name.trim(),
        quantity: "",
        line_total: "",
        destination: "shop",
        to_warehouse: "",
      },
    ]);
  }, []);

  const setLine = useCallback(
    (key, changes) =>
      setLines((current) =>
        current.map((line) => (line.key === key ? { ...line, ...changes } : line)),
      ),
    [],
  );

  const removeLine = useCallback(
    (key) => setLines((current) => current.filter((line) => line.key !== key)),
    [],
  );

  const reset = useCallback(() => setLines([]), []);

  const summary = useMemo(() => {
    const priced = lines.filter((line) => amount(line.line_total) !== null);

    return {
      total: priced.reduce((sum, line) => sum + amount(line.line_total), 0),
      unpriced: lines.length - priced.length,
      // Quantities are what cannot be left out; prices can come later
      complete:
        lines.length > 0 &&
        lines.every((line) => wholeQuantity(line.quantity) > 0 && !Number.isNaN(warehousePart(line))),
    };
  }, [lines]);

  /**
   * What POST /finance/receipts and /pos/deliveries take as `items`. Only the
   * dashboard, for a shop with a warehouse, sends where a line goes — the till
   * always receives into the shop.
   */
  const toItems = useCallback(
    ({ withWarehouse = false } = {}) =>
      lines.map((line) => ({
        ...(line.new_product ? { new_product: line.new_product } : { product_id: line.product_id }),
        quantity: wholeQuantity(line.quantity),
        ...(amount(line.line_total) !== null ? { line_total: amount(line.line_total) } : {}),
        ...(withWarehouse && warehousePart(line) > 0 ? { warehouse_quantity: warehousePart(line) } : {}),
      })),
    [lines],
  );

  return { lines, addProduct, addNewProduct, setLine, removeLine, reset, summary, toItems };
};
